/**
 * AquaFlavor — Product Page Controller
 * Handles: variant selection, gallery, bundle upsell, AJAX cart, sticky ATC, countdown
 */

(function () {
  'use strict';

  /* ─── UTILITIES ──────────────────────────────────────── */
  const qs  = (sel, ctx = document) => ctx.querySelector(sel);
  const qsa = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
  const fmt = (cents) => {
    const n = (cents / 100).toFixed(2);
    return '$' + n.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + ' AUD';
  };

  /* ─── PRODUCT DATA (injected by Liquid) ─────────────── */
  const productData = window.__AF_PRODUCT__;
  if (!productData) return;

  const { variants, options } = productData;
  let currentVariant = productData.currentVariant;

  /* ─── VARIANT MATCHING ───────────────────────────────── */
  function findVariant(selectedOptions) {
    return variants.find((v) =>
      v.options.every((opt, i) => opt === selectedOptions[i])
    ) || null;
  }

  function getSelectedOptions() {
    return options.map((_, i) => {
      const radio = qs(`input[name="option${i + 1}"]:checked`);
      const select = qs(`select[name="option${i + 1}"]`);
      return (radio || select)?.value || '';
    });
  }

  /* ─── UPDATE UI ON VARIANT CHANGE ───────────────────── */
  function onVariantChange() {
    const selectedOpts = getSelectedOptions();
    const variant = findVariant(selectedOpts);
    if (!variant) return;
    currentVariant = variant;

    // Price
    const priceEl = qs('.afp-price-main');
    const compareEl = qs('.afp-price-compare');
    const saveEl = qs('.afp-price-save');
    if (priceEl) priceEl.textContent = fmt(variant.price);
    if (compareEl && variant.compare_at_price > variant.price) {
      compareEl.textContent = fmt(variant.compare_at_price);
      compareEl.hidden = false;
      const pct = Math.round((1 - variant.price / variant.compare_at_price) * 100);
      if (saveEl) { saveEl.textContent = `Save ${pct}%`; saveEl.hidden = false; }
    } else {
      if (compareEl) compareEl.hidden = true;
      if (saveEl) saveEl.hidden = true;
    }

    // ATC button state
    const atcBtn = qs('.afp-atc-btn');
    const stickyBtn = qs('.afp-sticky-btn');
    if (atcBtn) {
      atcBtn.disabled = !variant.available;
      atcBtn.textContent = variant.available
        ? atcBtn.dataset.available
        : atcBtn.dataset.soldout;
    }
    if (stickyBtn) {
      stickyBtn.disabled = !variant.available;
    }

    // Variant ID hidden input
    const idInput = qs('[name="id"]');
    if (idInput) idInput.value = variant.id;

    // URL update (no page reload)
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    history.replaceState({}, '', url.toString());

    // Gallery image swap
    if (variant.featured_image) {
      swapMainImage(variant.featured_image.src);
    }

    // Stock label
    const stockEl = qs('.afp-stock-label');
    if (stockEl && variant.inventory_management) {
      if (!variant.available) {
        stockEl.textContent = 'Sold out';
        stockEl.className = 'afp-stock-label afp-stock-out';
      } else if (variant.inventory_quantity <= 10) {
        stockEl.textContent = `Only ${variant.inventory_quantity} left`;
        stockEl.className = 'afp-stock-label afp-stock-low';
      } else {
        stockEl.textContent = 'In stock — ships in 24h';
        stockEl.className = 'afp-stock-label afp-stock-in';
      }
    }
  }

  /* ─── GALLERY ─────────────────────────────────────────── */
  function initGallery() {
    const mainImg = qs('.afp-gallery-main img');
    const thumbs  = qsa('.afp-thumb');

    function setActive(thumb) {
      thumbs.forEach((t) => t.classList.remove('active'));
      thumb.classList.add('active');
    }

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const src = thumb.dataset.src;
        if (mainImg && src) {
          swapMainImage(src);
          setActive(thumb);
        }
      });
    });

    // Pinch-zoom hint on mobile (no-op, native handles it)
  }

  function swapMainImage(src) {
    const mainImg = qs('.afp-gallery-main img');
    if (!mainImg) return;
    mainImg.style.opacity = '0';
    mainImg.style.transform = 'scale(0.97)';
    setTimeout(() => {
      // If already size-suffixed (from image_url filter), use as-is; otherwise add _1200x
      const sized = /_\d+x/.test(src) ? src : src.replace(/\.(jpg|png|webp)(\?|$)/i, '_1200x.$1$2');
      mainImg.src = sized;
      mainImg.style.opacity = '1';
      mainImg.style.transform = 'scale(1)';
    }, 160);
  }

  /* ─── QUANTITY STEPPER ───────────────────────────────── */
  function initQtyStepper() {
    const input = qs('.afp-qty-input');
    if (!input) return;
    qs('.afp-qty-dec')?.addEventListener('click', () => {
      if (input.value > 1) input.value = +input.value - 1;
    });
    qs('.afp-qty-inc')?.addEventListener('click', () => {
      input.value = +input.value + 1;
    });
  }

  /* ─── BUNDLE UPSELL ──────────────────────────────────── */
  function initBundle() {
    const bundleItems = qsa('.afp-bundle-item');
    bundleItems.forEach((item) => {
      const cb = qs('input[type="checkbox"]', item);
      if (!cb) return;
      cb.addEventListener('change', () => {
        item.classList.toggle('selected', cb.checked);
        updateBundleTotal();
      });
      // Quantity in bundle item
      qs('.afp-bundle-dec', item)?.addEventListener('click', () => {
        const qty = qs('.afp-bundle-qty', item);
        if (qty && +qty.value > 1) qty.value = +qty.value - 1;
        updateBundleTotal();
      });
      qs('.afp-bundle-inc', item)?.addEventListener('click', () => {
        const qty = qs('.afp-bundle-qty', item);
        if (qty) qty.value = +qty.value + 1;
        updateBundleTotal();
      });
    });
  }

  function updateBundleTotal() {
    const basePrice = currentVariant?.price || 0;
    let bundleExtra = 0;
    qsa('.afp-bundle-item').forEach((item) => {
      const cb = qs('input[type="checkbox"]', item);
      if (cb?.checked) {
        const price = parseInt(item.dataset.price || 0, 10);
        const qty = parseInt(qs('.afp-bundle-qty', item)?.value || 1, 10);
        bundleExtra += price * qty;
      }
    });
    const totalEl = qs('.afp-bundle-total');
    if (totalEl) totalEl.textContent = fmt(basePrice + bundleExtra);
  }

  /* ─── AJAX ADD TO CART ───────────────────────────────── */
  async function addToCart(items) {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error('Cart error');
      const data = await res.json();
      await refreshCartCount();
      return data;
    } catch (err) {
      console.error('AquaFlavor cart error:', err);
      throw err;
    }
  }

  async function refreshCartCount() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      qsa('.js-cart-count').forEach((el) => { el.textContent = cart.item_count; });
      // Trigger cart drawer open if theme supports it
      document.dispatchEvent(new CustomEvent('af:cart:updated', { detail: cart }));
    } catch (_) {}
  }

  function initATCForm() {
    const form = qs('#afp-form');
    if (!form) return;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const btn = qs('.afp-atc-btn', form);
      if (!btn || btn.disabled) return;

      const variantId = parseInt(qs('[name="id"]', form)?.value, 10);
      const qty = parseInt(qs('[name="quantity"]', form)?.value || 1, 10);
      if (!variantId) return;

      // Build items array (main product + bundle)
      const items = [{ id: variantId, quantity: qty }];

      qsa('.afp-bundle-item').forEach((item) => {
        const cb = qs('input[type="checkbox"]', item);
        if (cb?.checked) {
          const bundleId = parseInt(item.dataset.variantId, 10);
          const bundleQty = parseInt(qs('.afp-bundle-qty', item)?.value || 1, 10);
          if (bundleId) items.push({ id: bundleId, quantity: bundleQty });
        }
      });

      // Loading state
      const origText = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding…';
      btn.classList.add('loading');

      try {
        await addToCart(items);
        btn.textContent = '✓ Added to Cart!';
        btn.classList.remove('loading');
        btn.classList.add('success');

        // Open cart drawer
        const cartTrigger = qs('.js-toggle-cart, .js-mini-cart, [data-cartmini]');
        if (cartTrigger) cartTrigger.click();

        setTimeout(() => {
          btn.disabled = false;
          btn.textContent = origText;
          btn.classList.remove('success');
        }, 2500);
      } catch (_) {
        btn.disabled = false;
        btn.textContent = origText;
        btn.classList.remove('loading');
      }
    });
  }

  /* ─── STICKY ATC BAR ─────────────────────────────────── */
  function initStickyATC() {
    const bar    = qs('.afp-sticky-bar');
    const anchor = qs('.afp-atc-btn');
    if (!bar || !anchor) return;

    const obs = new IntersectionObserver(([entry]) => {
      bar.classList.toggle('visible', !entry.isIntersecting);
    }, { threshold: 0 });
    obs.observe(anchor);

    qs('.afp-sticky-btn')?.addEventListener('click', () => {
      qs('.afp-atc-btn')?.click();
    });
  }

  /* ─── COUNTDOWN TIMER ────────────────────────────────── */
  function initCountdown() {
    const el = qs('.afp-countdown');
    if (!el) return;
    const endTime = parseInt(el.dataset.end, 10);
    if (!endTime) return;

    function tick() {
      const diff = endTime - Date.now();
      if (diff <= 0) { el.closest('.afp-urgency-bar')?.remove(); return; }
      const h = Math.floor(diff / 3600000).toString().padStart(2, '0');
      const m = Math.floor((diff % 3600000) / 60000).toString().padStart(2, '0');
      const s = Math.floor((diff % 60000) / 1000).toString().padStart(2, '0');
      const hEl = qs('.afp-cd-h', el);
      const mEl = qs('.afp-cd-m', el);
      const sEl = qs('.afp-cd-s', el);
      if (hEl) hEl.textContent = h;
      if (mEl) mEl.textContent = m;
      if (sEl) sEl.textContent = s;
    }
    tick();
    setInterval(tick, 1000);
  }

  /* ─── IMAGE ZOOM (CSS-driven, JS positions) ──────────── */
  function initZoom() {
    const wrapper = qs('.afp-gallery-main');
    const img     = qs('.afp-gallery-main img');
    if (!wrapper || !img) return;

    wrapper.addEventListener('mousemove', (e) => {
      const rect = wrapper.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      img.style.transformOrigin = `${x}% ${y}%`;
    });
    wrapper.addEventListener('mouseenter', () => { img.style.transform = 'scale(1.6)'; });
    wrapper.addEventListener('mouseleave', () => { img.style.transform = 'scale(1)'; });
  }

  /* ─── OPTION SWATCHES ────────────────────────────────── */
  function initSwatches() {
    qsa('.afp-swatch').forEach((swatch) => {
      swatch.addEventListener('click', () => {
        const name = swatch.dataset.option;
        qsa(`.afp-swatch[data-option="${name}"]`).forEach((s) => s.classList.remove('active'));
        swatch.classList.add('active');
        // sync hidden radio
        const radio = qs(`input[value="${swatch.dataset.value}"]`);
        if (radio) { radio.checked = true; radio.dispatchEvent(new Event('change', { bubbles: true })); }
      });
    });
  }

  /* ─── ACCORDION TABS ─────────────────────────────────── */
  function initAccordion() {
    qsa('.afp-tab-head').forEach((head) => {
      const body = head.nextElementSibling;
      if (!body) return;
      body.style.maxHeight = '0';
      body.style.overflow = 'hidden';
      body.style.transition = 'max-height 0.35s ease';

      head.addEventListener('click', () => {
        const isOpen = head.classList.contains('open');
        qsa('.afp-tab-head.open').forEach((h) => {
          h.classList.remove('open');
          if (h.nextElementSibling) h.nextElementSibling.style.maxHeight = '0';
        });
        if (!isOpen) {
          head.classList.add('open');
          body.style.maxHeight = body.scrollHeight + 'px';
        }
      });
    });

    // Open first tab
    qs('.afp-tab-head')?.click();
  }

  /* ─── RECENTLY VIEWED ────────────────────────────────── */
  function trackRecentlyViewed() {
    if (!productData.handle) return;
    try {
      const key = 'af_recently_viewed';
      const list = JSON.parse(localStorage.getItem(key) || '[]');
      const filtered = list.filter((h) => h !== productData.handle);
      filtered.unshift(productData.handle);
      localStorage.setItem(key, JSON.stringify(filtered.slice(0, 8)));
    } catch (_) {}
  }

  /* ─── SCROLL REVEAL ──────────────────────────────────── */
  function initReveal() {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('af-reveal--visible'); obs.unobserve(e.target); }
      });
    }, { threshold: 0.1 });
    qsa('.af-reveal').forEach((el) => obs.observe(el));
  }

  /* ─── INIT ───────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', () => {
    initGallery();
    initQtyStepper();
    initBundle();
    initATCForm();
    initStickyATC();
    initCountdown();
    initZoom();
    initSwatches();
    initReveal();
    trackRecentlyViewed();

    // Option change listeners
    qsa('input[name^="option"], select[name^="option"]').forEach((el) => {
      el.addEventListener('change', onVariantChange);
    });

    // Initial bundle total
    updateBundleTotal();

    // Init accordion with delay so DOM settles
    setTimeout(initAccordion, 50);
  });

})();
